import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';

/**
 * Look of one site, stored as brand roles. Empty or missing fields mean "keep the default system look".
 * Hover states, light tints and readable text are derived from these, never chosen by the tenant.
 */
export interface SiteTheme {
  primary_color?: string; // buttons, links, selected tabs, progress bars, focus rings
  sidebar_background?: string; // sidebar panel
  sidebar_text?: string; // sidebar labels and icons
  sidebar_active?: string; // selected sidebar item; falls back to primary_color
  page_background?: string; // area behind the page content
}

export type UISite = 'admin' | 'learner'|'auth';

// Default system look; shown as placeholders and in the preview when a field is empty
export const DEFAULT_SITE_THEME: Required<SiteTheme> = {
  primary_color: '#696cff', // primary-700
  sidebar_background: '#ffffff',
  sidebar_text: '#4b5563', // gray-600
  sidebar_active: '#696cff', // primary-700
  page_background: '#f9fafb', // gray-50
};

export interface ThemePreset {
  name: string;
  theme: Required<SiteTheme>;
}

// Ready-made themes; "System Default" clears every field so the original look is kept exactly
export const THEME_PRESETS: ThemePreset[] = [
  {
    name: 'System Default',
    theme: { primary_color: '', sidebar_background: '', sidebar_text: '', sidebar_active: '', page_background: '' },
  },
  {
    name: 'Ocean',
    theme: { primary_color: '#0369a1', sidebar_background: '#0c4a6e', sidebar_text: '#e0f2fe', sidebar_active: '#38bdf8', page_background: '#f0f9ff' },
  },
  {
    name: 'Forest',
    theme: { primary_color: '#15803d', sidebar_background: '#ffffff', sidebar_text: '#374151', sidebar_active: '#15803d', page_background: '#f7fee7' },
  },
  {
    name: 'Slate',
    theme: { primary_color: '#334155', sidebar_background: '#1e293b', sidebar_text: '#cbd5e1', sidebar_active: '#f8fafc', page_background: '#f8fafc' },
  },
  {
    name: 'Sunset',
    theme: { primary_color: '#c2410c', sidebar_background: '#fff7ed', sidebar_text: '#7c2d12', sidebar_active: '#c2410c', page_background: '#fffbf5' },
  },
  {
    name: 'Royal',
    theme: { primary_color: '#6d28d9', sidebar_background: '#2e1065', sidebar_text: '#ddd6fe', sidebar_active: '#c4b5fd', page_background: '#faf5ff' },
  },
];

export interface UICustomization {
  _id?: string;
  id?: string;
  admin: SiteTheme;
  learner: SiteTheme;
  auth: SiteTheme;
  createdAt?: string;
  updatedAt?: string;
}

export interface SaveUICustomizationPayload {
  admin?: SiteTheme;
  learner?: SiteTheme;
  auth?:SiteTheme
}

class UICustomizationService {
  async getUICustomization() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.UI_CUSTOMIZATION.GET);
      return response.data;
    } catch (error) {
      console.error('Error fetching UI customization:', error);
      throw error;
    }
  }

  async saveUICustomization(payload: SaveUICustomizationPayload) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.UI_CUSTOMIZATION.SAVE, payload);
      return response.data;
    } catch (error) {
      console.error('Error saving UI customization:', error);
      throw error;
    }
  }
}

export const uiCustomizationService = new UICustomizationService();
